export default function CelebrationDestinationsSection() {
  const stats = [
    {
      value: "42%",
      label: "of Celebration requests are Disney-affiliated travel with under 3 weeks notice",
    },
    {
      value: "2–4 weeks",
      label: "average notice for cast member international assignments and park transfers",
    },
    {
      value: "Mar–Apr",
      label: "peak spring break cruise season — passport renewals spike before Port Canaveral departures",
    },
    {
      value: "48-hour",
      label: "shortest Disney corporate departure window we've accommodated for a Celebration cast member",
    },
  ];

  const destinations = [
    {
      icon: "ri-map-pin-2-line",
      name: "Disney International Parks — Paris, Tokyo, Shanghai, Hong Kong",
      context: "The most distinctive travel pattern for Celebration residents. Cast members travel to international Disney parks for training, seasonal transfers, and corporate events. Families visit Disneyland Paris for European vacations, Tokyo Disney Resort for multi-generational trips, and Shanghai Disney Resort for bucket-list experiences. These trips are often planned with 2–4 weeks of notice for cast members and 2–6 months for family vacations.",
      urgency: "Cast member assignments often arrive with 2–3 weeks of notice — insufficient time for standard passport processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "The Bahamas & Caribbean",
      context: "The most common leisure destination for Celebration families — accessed primarily through Disney Cruise Line departures from Port Canaveral. Disney operates 3-night, 4-night, and 7-night Caribbean itineraries year-round, with stops at Castaway Cay (Disney's private island), Nassau, and various Eastern and Western Caribbean ports. All passengers, including infants, require valid passports.",
      urgency: "Cruise embarkation deadlines are immovable — passport issues discovered days before sailing require emergency processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "United Kingdom & Ireland",
      context: "London, Edinburgh, and Dublin are common destinations for Celebration's medical professionals attending conferences at the Royal College of Physicians, the European Society of Cardiology, and various pediatric and oncology congresses. Disney also operates Disneyland Paris, which draws Celebration families extending their park loyalty to Europe. These trips are typically planned 4–12 weeks in advance.",
      urgency: "Conference registration deadlines and abstract submissions create fixed travel timelines that cannot be moved",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Mexico & Latin America",
      context: "Cancun, Riviera Maya, and Los Cabos are staple destinations for Celebration families seeking beach vacations within a short flight. MCO offers direct and connecting service to major Mexican resorts. Business travelers also fly to Mexico City for corporate meetings. Mexico requires six months of passport validity for entry. Spring break bookings surge in January, creating passport discovery spikes in February.",
      urgency: "Spring break bookings surge in January — passport discoveries spike in February and March for Celebration families",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Canada",
      context: "Toronto, Vancouver, and Montreal draw Celebration medical professionals for North American conferences and training programs. Vancouver hosts major pediatric and cardiology events that attract AdventHealth Celebration physicians. Toronto is a frequent destination for Disney corporate meetings and training. Canada remains one of the easiest international destinations for U.S. passport holders, but still requires valid documentation.",
      urgency: "Conference registration typically opens 8–12 weeks before the event — check passport validity when you register, not when you book flights",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Spain, Italy & France",
      context: "Barcelona, Rome, and Paris are top European destinations for Celebration's medical conference attendees and family vacationers. The European Society of Cardiology meets in Barcelona and Milan. Disney's international presence in Paris draws families. These destinations require six months of passport validity and are popular during summer break and medical conference seasons.",
      urgency: "European destinations enforce the six-month passport validity rule strictly — check expiration 6+ months before travel",
    },
  ];

  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Where Celebration Travelers Go</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Popular International Destinations from Celebration
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Celebration travelers head to Disney's international parks, the Caribbean for cruises, Europe for conferences and vacations, and Latin America for family getaways. These are the destinations that create the most passport urgency for our Celebration clients.
          </p>
        </div>

        {/* Stat blocks */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {stats.map((stat, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200 text-center">
              <p className="font-serif text-2xl md:text-3xl font-bold text-[#1A365D] mb-1">{stat.value}</p>
              <p className="text-gray-500 text-xs leading-relaxed">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Destination cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {destinations.map((dest, i) => (
            <div
              key={i}
              className="bg-[#F7F8FA] rounded-lg border border-gray-200 p-6 hover:border-[#F6C344] transition-all duration-200 flex flex-col"
            >
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 flex-shrink-0">
                  <i className={`${dest.icon} text-[#1A365D] text-sm`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{dest.name}</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-1">{dest.context}</p>
              <div className="bg-white rounded-md p-3 border border-gray-200">
                <p className="text-[#F6C344] text-xs font-semibold mb-0.5"><i className="ri-time-line mr-1"></i>Urgency Pattern</p>
                <p className="text-gray-500 text-xs leading-relaxed">{dest.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Internal links */}
        <div className="text-center">
          <p className="text-gray-400 text-xs mb-3">Serving travelers throughout Central Florida</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { name: "Orlando", href: "/orlando" },
              { name: "Windermere", href: "/windermere" },
              { name: "Winter Garden", href: "/winter-garden" },
              { name: "Winter Park", href: "/winter-park" },
              { name: "Lake Mary", href: "/lake-mary" },
              { name: "Tampa", href: "/tampa" },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5"
              >
                {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}